/**
 * MindMapTemplates Component
 * Dialog for selecting preset mind map templates
 */

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SavingsIcon from "@mui/icons-material/Savings";
import { MINDMAP_TEMPLATES, MindMapTemplate } from "./mindMapTypes";

interface MindMapTemplatesProps {
  open: boolean;
  onClose: () => void;
  onSelect: (template: MindMapTemplate) => void;
}

const getTemplateIcon = (iconName: string) => {
  const iconProps = { sx: { fontSize: 40, color: "primary.main" } };
  switch (iconName) {
    case "lightbulb":
      return <LightbulbIcon {...iconProps} />;
    case "assignment":
      return <AssignmentIcon {...iconProps} />;
    case "savings":
      return <SavingsIcon {...iconProps} />;
    case "add":
    default:
      return <AddIcon {...iconProps} />;
  }
};

const MindMapTemplates: React.FC<MindMapTemplatesProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const handleSelect = (template: MindMapTemplate) => {
    onSelect(template);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Choose a Template</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select a template to get started quickly, or choose Blank to start
          from scratch.
        </Typography>
        <Grid container spacing={2}>
          {MINDMAP_TEMPLATES.map((template) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={template.id}>
              <Card
                sx={{
                  height: "100%",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleSelect(template)}
                  sx={{ height: "100%", p: 2 }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      height: "100%",
                      p: 0,
                    }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        borderRadius: "50%",
                        bgcolor: "action.hover",
                      }}
                    >
                      {getTemplateIcon(template.icon)}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {template.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {template.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MindMapTemplates;
